import { Trip } from "@/types/trip"
import { cn } from "cn"

import { selectIsXS } from "@/lib/storage/app-slice"
import { useAppDispatch, useAppSelector } from "@/lib/storage/hooks"
import {
  selectEventCount,
  selectHasAnyEvents,
} from "@/lib/storage/itinerary-slice"
import tripSlice from "@/lib/storage/trip-slice"
import { openTripSheet } from "@/lib/storage/trip-thunks"
import { selectHideCreateTrip } from "@/lib/storage/ui-settings-slice"
import IconAddTrip from "@/components/icons/add-trip"
import IconBaggage from "@/components/icons/baggage"
import { loadTripSheetPage } from "./trip/lazy-trip-sheet-page"
import { Badge } from "./ui/badge"
import { Button } from "./ui/button"
import { IconButton } from "./ui/icon-button"

export function ChatTripButton({
  trip,
  className,
}: {
  trip: Trip
  className?: string
}) {
  const dispatch = useAppDispatch()
  const isXS = useAppSelector(selectIsXS)
  const eventCount = useAppSelector(selectEventCount)
  const hasEvents = useAppSelector(selectHasAnyEvents)
  const hideCreateTrip = useAppSelector(selectHideCreateTrip)

  const tripIsUserCreatedOrHasEvents = trip.user_created || hasEvents

  const handleClick = () => {
    if (tripIsUserCreatedOrHasEvents) {
      void dispatch(openTripSheet())
    } else {
      dispatch(
        tripSlice.actions.openCreateTripDialog({
          prefillTripData: "current",
          showSheet: true,
        })
      )
    }
  }

  const warmTripSheet = () => {
    if (tripIsUserCreatedOrHasEvents) {
      void loadTripSheetPage()
    }
  }

  if (hideCreateTrip && !tripIsUserCreatedOrHasEvents) {
    return null
  }

  const TripIcon = tripIsUserCreatedOrHasEvents ? IconBaggage : IconAddTrip

  return isXS ? (
    <div className={cn("relative flex", className)}>
      <IconButton
        icon={{ icon: TripIcon, size: 24 }}
        label="Trip"
        variant="ghost"
        size="sm"
        onClick={handleClick}
        onPointerEnter={warmTripSheet}
        onFocus={warmTripSheet}
      />
      {tripIsUserCreatedOrHasEvents && (
        <Badge
          color="accent"
          className={cn(
            "pointer-events-none absolute top-1.5 -right-1 px-1",
            eventCount > 9 && "-right-1.5"
          )}
        >
          {eventCount}
        </Badge>
      )}
    </div>
  ) : (
    <Button
      size="sm"
      iconBefore={TripIcon}
      variant={tripIsUserCreatedOrHasEvents ? "outline" : "primary"}
      className={className}
      onClick={handleClick}
      onPointerEnter={warmTripSheet}
      onFocus={warmTripSheet}
    >
      {tripIsUserCreatedOrHasEvents ? (
        <>
          Trip
          <Badge color="accent" className="-mr-1 ml-0.5">
            {eventCount}
          </Badge>
        </>
      ) : (
        <>Create a trip</>
      )}
    </Button>
  )
}
